import { LineItemDataType } from "@redotech/redo-flows-core-schema-common/type-system/data-types/trackable-types";
import {
  SchemaInstance,
  defineSchema,
} from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { MOCK_PRODUCT_IMAGE_URL } from "@redotech/redo-marketing-mock-common/mock-values";
import { redoCustomerIdFields } from "../common-fields";

/**
 * Schema shared by every HSA/FSA (Gale) order email trigger. A single schema
 * type backs all three triggers (eligible receipt, LMN pending, LMN approved);
 * trigger-specific fields are optional and only populated for the triggers that
 * have them (e.g. `amountCharged` once the card has been charged).
 */
export const hsaOrderSchema = defineSchema({
  fields: {
    customerName: {
      dataType: "Name",
      documentation: "The full name of the customer.",
    },
    customerFirstName: {
      dataType: "Maybe Text",
      documentation: "The first name of the customer.",
      hideFromConditionStep: true,
    },
    customerEmail: {
      dataType: "Email",
      documentation: "The email address of the customer.",
    },
    orderNumber: {
      dataType: "Text",
      documentation: "The order number (e.g. #1001).",
    },
    storeName: { dataType: "Text", documentation: "The name of the store." },
    eligibleLineItems: {
      dataType: "Multiple Line Item",
      documentation:
        "The HSA/FSA-eligible products in the order, shown on the itemized receipt.",
    },
    eligibleSubtotal: {
      dataType: "Text",
      documentation:
        "The formatted subtotal of the HSA/FSA-eligible products (e.g. $42.00).",
    },
    lmnLineItems: {
      dataType: "Maybe Multiple Line Item",
      documentation:
        "Dual-purpose products that require a Letter of Medical Necessity (LMN) — pending while the LMN is being generated, authorized once it returns.",
    },
    amountCharged: {
      dataType: "Maybe Text",
      documentation:
        "The formatted amount charged to the customer's HSA/FSA card. Present once the card has been charged.",
    },
    orderDetailsLink: {
      dataType: "Maybe Url",
      documentation: "The link to view the order details.",
    },
    ...redoCustomerIdFields,
  },
});

const exampleHsaLineItem: LineItemDataType["Line Item"] = {
  product: {
    id: 1,
    title: "Example Health Product",
    variantTitle: "Default",
    image: { src: MOCK_PRODUCT_IMAGE_URL },
    price: 42.0,
    priceSet: { presentment_money: { amount: 42.0, currency_code: "USD" } },
    isRedo: false,
    requiresShipping: true,
    tags: [],
    properties: {},
    sku: "HSA-SKU-1",
    green_return: false,
  },
  quantity: 1,
};

export const exampleHsaOrderSchemaInstance: SchemaInstance<
  typeof hsaOrderSchema
> = {
  customerName: "John Doe",
  customerFirstName: "John",
  customerEmail: "johndoe@example.com",
  orderNumber: "#1001",
  storeName: "Example Store",
  eligibleLineItems: [exampleHsaLineItem],
  eligibleSubtotal: "$42.00",
  lmnLineItems: undefined,
  amountCharged: undefined,
  orderDetailsLink: "https://example.com/order-details",
  redoCustomerId: "66b131782f9829bd1ed05bd7",
};
